import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { useNavigate, useLocation } from "react-router-dom";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../constants";
import {
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiCpu,
  FiDatabase,
  FiFileText,
  FiFlag,
  FiGrid,
  FiSend,
  FiSliders,
  FiUsers,
} from "react-icons/fi";
import TopStatusBanner from "./TopStatusBanner";
import "../styles/NoteDashboard.css";

function removeUserScopedData() {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload && payload.username) {
      const key = `plantillasSeleccionadas_${payload.username}`;
      localStorage.removeItem(key);
    }
  } catch {
    // ignore decoding errors
  }
}


function Note({ note, onDelete, onEdit }) {
  // Clave de storage por usuario (si existe) + id de la nota
  const storageKey = useMemo(() => {
    const u = localStorage.getItem('username') || 'anon';
    return `note_expanded_${u}_${note?.id}`;
  }, [note?.id]);
  const [expanded, setExpanded] = useState(false);
  const COLLAPSED_MAX = 160; // altura en px cuando está colapsado (mostrar más pequeño de largo)

  // Cargar estado desde localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === '1') setExpanded(true);
    } catch {}
  }, [storageKey]);

  // Guardar estado en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, expanded ? '1' : '0');
    } catch {}
  }, [expanded, storageKey]);

  return (
    <div className="note-card">
      <div className="note-header">
        <h3>{note.title}</h3>
        <button className="note-delete" onClick={() => onDelete(note.id)}>
          &#10006;
        </button>
      </div>
      <div style={{ position: "relative" }}>
        <div
          className="note-content"
          style={{
            maxHeight: expanded ? "none" : COLLAPSED_MAX,
            overflow: expanded ? "visible" : "hidden",
            transition: "max-height 0.2s ease",
          }}
          dangerouslySetInnerHTML={{ __html: note.content }}
        />
        {!expanded && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 40,
              background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(26,31,37,0.0) 0%, rgba(26,31,37,0.06) 40%, rgba(26,31,37,0.12) 100%)",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
      {/* Botonera siempre visible, integrada al recuadro */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "#f3f4f6",
            color: "#374151",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          {expanded ? "Contraer" : "Expandir"}
        </button>
        <button
          onClick={() => onEdit(note)}
          style={{
            background: "#f3f4f6",
            color: "#374151",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          Editar
        </button>
      </div>
    </div>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const isActive = (p) => {
    if (p === "/contactos") return path === "/contactos" || path.startsWith("/contactos/");
    if (p === "/plantillas") return path === "/plantillas" || path.startsWith("/plantillas");
    if (p === "/mensajes" || p === "/mensajes-coexistencia") {
      return path === "/mensajes" || path === "/mensajes-coexistencia" || path.startsWith("/mensajes");
    }
    if (p === "/integraciones") return path === "/integraciones" || path.startsWith("/integraciones/");
    return path === p;
  };

  const [features, setFeatures] = useState([]);
  const [integrationMode, setIntegrationMode] = useState('no_oficial');
  const [profile, setProfile] = useState({ email: "", plan_state: "unknown" });
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });
  const accountMenuRef = useRef(null);

  const storedUsername = useMemo(() => {
    try {
      return (localStorage.getItem("username") || "").trim();
    } catch {
      return "";
    }
  }, []);

  // Cargar feature flags del usuario autenticado
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data } = await api.get("user/features/");
        const f = Array.isArray(data?.features) ? data.features : [];
        if (isMounted) setFeatures(f);
      } catch (_) {
        if (isMounted) setFeatures([]);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!accountMenuRef.current) return;
      if (!accountMenuRef.current.contains(e.target)) setAccountMenuOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("sidebar_collapsed", sidebarCollapsed ? "1" : "0");
    } catch {
      // ignore
    }
  }, [sidebarCollapsed]);

  // Cargar modo de integración
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data } = await api.get("integration/connection-status/");
        if (isMounted && data?.mode) {
          setIntegrationMode(data.mode);
        }
      } catch (_) {
        if (isMounted) setIntegrationMode('no_oficial');
      }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data } = await api.get("user/profile/");
        if (!isMounted) return;
        setProfile({
          email: (data?.email || "").trim(),
          plan_state: String(data?.plan_state || "unknown"),
        });
      } catch {
        if (isMounted) setProfile({ email: "", plan_state: "unknown" });
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const menuItems = React.useMemo(() => {
    const baseItems = [
      // Orden solicitado
      { path: "/resumen", label: "Resumen", Icon: FiGrid },
      { path: "/contactos", label: "Contactos", Icon: FiUsers },
      // Plantillas condicional según modo
      integrationMode === 'coexistencia' 
        ? { path: "/plantillas", label: "Plantillas Coexistencia", Icon: FiFileText }
        : { path: "/plantillas", label: "Plantillas", Icon: FiFileText },
      // Reemplazar "Campañas" por "Campañas Coexistencia" cuando esté en modo coexistencia
      integrationMode === 'coexistencia'
        ? { path: "/mensajes-coexistencia", label: "Campañas Coexistencia", Icon: FiSend }
        : { path: "/mensajes", label: "Campañas", Icon: FiSend },
      { path: "/conexion", label: "Importar BD", Icon: FiDatabase },
      { path: "/integraciones", label: "Integraciones", Icon: FiSliders },
      // Oculto temporalmente WhatsApp QR por solicitud (no mostrar en menú)
      { path: "/soporte", label: "Google Calendar", Icon: FiCalendar },
      { path: "/roadmap", label: "Roadmap", Icon: FiFlag },
      // (resto del menú)
    ];
    const items = [...baseItems];
    if (features.includes('ai_agent')) {
      items.splice(3, 0, { path: "/agente-ia", label: "Agente de IA", Icon: FiCpu });
    }
    return items;
  }, [features, integrationMode]);

  const accountEmail = profile.email || storedUsername || "usuario@wavesales.com";
  const accountInitials = useMemo(() => {
    const source = accountEmail.split("@")[0] || accountEmail;
    const parts = source.split(/[._\s-]+/).filter(Boolean);
    if (parts.length === 0) return "WS";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }, [accountEmail]);

  const planLabel = useMemo(() => {
    if (profile.plan_state === "active") return "Plan Activo";
    if (profile.plan_state === "grace") return "Periodo de Gracia";
    if (profile.plan_state === "expired") return "Plan Expirado";
    return "Plan Activo";
  }, [profile.plan_state]);

  const handleLogout = () => {
    removeUserScopedData();
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem("username");
    navigate("/login");
  };

  return (
    <>
    <TopStatusBanner floating />
    <aside className={`${sidebarCollapsed ? "w-[88px]" : "w-[282px]"} min-h-screen bg-[#070d1a] border-r border-[#1a2538] flex flex-col shadow-xl transition-all duration-200`}>
      <div className="px-6 py-6 border-b border-[#1a2538]">
        <button
          onClick={() => navigate('/')}
          className={`flex items-center ${sidebarCollapsed ? "justify-center" : "gap-3"} text-left hover:opacity-90 transition w-full`}
          title="Ir a inicio"
        >
          <img src="/images/logowave.png?v=1" alt="WaveSales" className="w-10 h-10 object-contain shrink-0" />
          {!sidebarCollapsed && (
            <div>
              <p className="text-white font-bold leading-tight">WaveSales</p>
              <p className="text-[#7f8ca5] text-sm leading-tight">WhatsApp Marketing</p>
            </div>
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1.5">
          {menuItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.Icon;
            return (
              <li key={item.path}>
                <button
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`w-full rounded-xl ${sidebarCollapsed ? "px-2 py-3" : "px-4 py-3"} transition-all duration-150 flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} ${
                    active
                      ? "bg-[#111a2f] border border-emerald-500/80 shadow-[0_0_0_1px_rgba(16,185,129,0.15)]"
                      : "border border-transparent hover:bg-[#111a2f]/70"
                  }`}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <span className={`flex items-center ${sidebarCollapsed ? "justify-center" : "gap-3"} min-w-0`}>
                    <span className={`text-[18px] ${active ? "text-emerald-400" : "text-[#8d9bb3]"}`}>
                      <Icon />
                    </span>
                    {!sidebarCollapsed && (
                      <span className={`font-medium text-sm truncate ${active ? "text-white" : "text-[#c6d0e0]"}`}>
                        {item.label}
                      </span>
                    )}
                  </span>
                  {!sidebarCollapsed && <FiChevronRight className={`${active ? "text-emerald-400" : "text-[#4a5a73]"}`} />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-auto border-t border-[#1a2538] p-4" ref={accountMenuRef}>
        <div className="relative">
          <button
            type="button"
            onClick={() => setAccountMenuOpen((v) => !v)}
            className={`w-full rounded-xl border border-transparent hover:border-[#203250] hover:bg-[#0c1426] px-2 py-2 transition flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} gap-2`}
            title={sidebarCollapsed ? accountEmail : undefined}
          >
            <span className={`flex items-center ${sidebarCollapsed ? "justify-center" : "gap-3"} min-w-0`}>
              <span className="w-10 h-10 rounded-full bg-[#102041] text-emerald-400 font-semibold flex items-center justify-center shrink-0">
                {accountInitials}
              </span>
              {!sidebarCollapsed && (
                <span className="min-w-0 text-left">
                  <span className="text-white text-sm font-semibold truncate block">{accountEmail}</span>
                  <span className="text-[#7f8ca5] text-xs">{planLabel}</span>
                </span>
              )}
            </span>
            {!sidebarCollapsed && <FiChevronDown className={`text-[#8da0bf] transition-transform ${accountMenuOpen ? "rotate-180" : ""}`} />}
          </button>

          {accountMenuOpen && (
            <div className="absolute left-0 bottom-full mb-2 w-[220px] bg-white border border-slate-200 rounded-xl shadow-xl z-30 overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setAccountMenuOpen(false);
                  navigate("/configuracion");
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-500"></span>
                Configuración
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
              >
                <span className="inline-block rotate-180">↪</span>
                Cerrar sesión
              </button>
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-[#1a2538] pt-3">
          <button
            type="button"
            onClick={() => setSidebarCollapsed((v) => !v)}
            className={`w-full rounded-lg px-3 py-2 ${sidebarCollapsed ? "text-center" : "text-left"} text-sm text-[#8fa3bf] transition hover:bg-[#0c1426] hover:text-[#d2def1]`}
            title={sidebarCollapsed ? "Expandir" : "Colapsar"}
          >
            <span className={`text-[16px] leading-none ${sidebarCollapsed ? "" : "mr-2"}`}>{sidebarCollapsed ? "›" : "‹"}</span>
            {!sidebarCollapsed && "Colapsar"}
          </button>
          {!sidebarCollapsed && <p className="mt-3 text-xs text-[#6f7f97]">© 2025 WaveSales</p>}
        </div>
      </div>
    </aside>
    </>
  );
}


const modules = {
  toolbar: {
    container: [
      [{ font: [] }, { size: [] }],
      ['bold','italic','underline','strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub'}, { script: 'super' }],
      [{ header: '1'}, { header: '2'}, 'blockquote','code-block'],
      [{ list: 'ordered'}, { list: 'bullet'}, { indent: '-1'}, { indent: '+1'}],
      [{ align: [] }],
      ['image','emoji'],
      ['clean']
    ]
  },
  'emoji-toolbar': true,
  'emoji-textarea': false,
  'emoji-shortname': true
};

const formats = [
  'font','size','bold','italic','underline','strike','color','background',
  'script','header','blockquote','code-block','list','indent','align','emoji','image','link','video'
];

export { Note, Sidebar, modules, formats };
